import { supabase } from './supabase';

export interface MeasurementField {
  name: string; // e.g., "length", "width", "gauge"
  label: string; // e.g., "Length", "Width", "Glass Thickness"
  type: 'number' | 'text';
  required: boolean;
  calculate?: boolean; // For area - whether to auto-calculate from length*width
  unit?: string; // Unit of measurement for this field (e.g., 'ft', 'in', 'm', 'cm')
}

export interface MeasurementPreset {
  id: string;
  business_id: string;
  name: string;
  category?: string | null;
  has_length: boolean; // Legacy - kept for backward compatibility
  has_width: boolean; // Legacy
  has_height: boolean; // Legacy
  has_area: boolean; // Legacy
  custom_fields?: MeasurementField[] | null; // New flexible field system
  unit: string;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export interface AddMeasurementPresetParams {
  business_id: string;
  name: string;
  category?: string | null;
  has_length?: boolean; // Legacy
  has_width?: boolean; // Legacy
  has_height?: boolean; // Legacy
  has_area?: boolean; // Legacy
  custom_fields?: MeasurementField[]; // New flexible field system
  unit?: string;
  notes?: string | null;
}

export interface UpdateMeasurementPresetParams {
  name?: string;
  category?: string | null;
  has_length?: boolean; // Legacy
  has_width?: boolean; // Legacy
  has_height?: boolean; // Legacy
  has_area?: boolean; // Legacy
  custom_fields?: MeasurementField[]; // New flexible field system
  unit?: string;
  notes?: string | null;
}

/**
 * Get all measurement presets for a business
 */
export async function getMeasurementPresets(businessId: string): Promise<MeasurementPreset[]> {
  if (!businessId) {
    throw new Error('Business ID is required');
  }

  const { data, error } = await supabase
    .from('measurement_presets')
    .select('*')
    .eq('business_id', businessId)
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching measurement presets:', error);
    throw error;
  }

  return (data || []) as MeasurementPreset[];
}

/**
 * Add a measurement preset
 */
export async function addMeasurementPreset(params: AddMeasurementPresetParams): Promise<MeasurementPreset> {
  if (!params.business_id) {
    throw new Error('Business ID is required');
  }
  if (!params.name || !params.name.trim()) {
    throw new Error('Preset name is required');
  }

  // Get current user for created_by
  const { data: { user } } = await supabase.auth.getUser();

  // Build custom_fields from legacy flags if custom_fields not provided
  let customFields = params.custom_fields;
  if (!customFields || customFields.length === 0) {
    customFields = [];
    if (params.has_length) {
      customFields.push({ name: 'length', label: 'Length', type: 'number', required: true, calculate: false });
    }
    if (params.has_width) {
      customFields.push({ name: 'width', label: 'Width', type: 'number', required: true, calculate: false });
    }
    if (params.has_height) {
      customFields.push({ name: 'height', label: 'Height', type: 'number', required: true, calculate: false });
    }
    if (params.has_area) {
      customFields.push({ name: 'area', label: 'Area', type: 'number', required: false, calculate: true });
    }
  }

  const { data, error } = await supabase
    .from('measurement_presets')
    .insert({
      business_id: params.business_id,
      name: params.name.trim(),
      category: params.category || null,
      has_length: params.has_length || false, // Keep for backward compatibility
      has_width: params.has_width || false,
      has_height: params.has_height || false,
      has_area: params.has_area || false,
      custom_fields: customFields,
      unit: params.unit || 'ft',
      notes: params.notes || null,
      created_by: user?.id || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding measurement preset:', error);
    throw error;
  }

  return data as MeasurementPreset;
}

/**
 * Update a measurement preset
 */
export async function updateMeasurementPreset(
  presetId: string,
  params: UpdateMeasurementPresetParams
): Promise<MeasurementPreset> {
  if (!presetId) {
    throw new Error('Preset ID is required');
  }

  const updateData: any = {};
  if (params.name !== undefined) updateData.name = params.name.trim();
  if (params.category !== undefined) updateData.category = params.category;
  if (params.has_length !== undefined) updateData.has_length = params.has_length;
  if (params.has_width !== undefined) updateData.has_width = params.has_width;
  if (params.has_height !== undefined) updateData.has_height = params.has_height;
  if (params.has_area !== undefined) updateData.has_area = params.has_area;
  if (params.custom_fields !== undefined) updateData.custom_fields = params.custom_fields;
  if (params.unit !== undefined) updateData.unit = params.unit;
  if (params.notes !== undefined) updateData.notes = params.notes;

  const { data, error } = await supabase
    .from('measurement_presets')
    .update(updateData)
    .eq('id', presetId)
    .select()
    .single();

  if (error) {
    console.error('Error updating measurement preset:', error);
    throw error;
  }

  return data as MeasurementPreset;
}

/**
 * Delete a measurement preset
 */
export async function deleteMeasurementPreset(presetId: string): Promise<void> {
  if (!presetId) {
    throw new Error('Preset ID is required');
  }

  const { error } = await supabase
    .from('measurement_presets')
    .delete()
    .eq('id', presetId);

  if (error) {
    console.error('Error deleting measurement preset:', error);
    throw error;
  }
}
